import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { subscribePush, unsubscribePush } from "../api/push";
import { vapidPublicKeyToApplicationServerKey } from "./vapidKey";

type PushSubscriptionState = "unsupported" | "prompt" | "granted" | "denied";

const webPushSubscriptionQueryKey = ["webPushSubscription"] as const;

/** Push API（Notification + serviceWorker）が利用可能か。 */
function isPushApiSupported(): boolean {
    return Boolean(window?.Notification && navigator?.serviceWorker);
}

/**
 * 通知権限APIの値を、フックの UI 用 state に変換する。
 * 標準の `default` が命名的にわかりづらく、意味的には `prompt` に近いので、そちらに変換して利用する。
 * @param permission - `Notification.permission`
 */
function notificationPermissionToUiState(
    permission: NotificationPermission
): "prompt" | "granted" | "denied" {
    switch (permission) {
        case "default":
            return "prompt";
        case "denied":
            return "denied";
        case "granted":
            return "granted";
        default:
            return "prompt";
    }
}

/** マウント時点の環境・権限から `PushSubscriptionState` の初期値を決める。 */
function getInitialPushSubscriptionState(): PushSubscriptionState {
    if (!isPushApiSupported()) {
        return "unsupported";
    }
    return notificationPermissionToUiState(Notification.permission);
}

/**
 * Push 購読が存在するかを取得する。
 * `granted` 以外のときは `false` を返す。
 */
async function fetchIsPushSubscribed(): Promise<boolean> {
    if (Notification.permission !== "granted") {
        return false;
    }
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    return sub != null;
}

/**
 * 既存購読があればバックエンドへ登録だけ行い、なければ VAPID で購読してから登録する。
 * @param registration - 準備済みの Service Worker 登録
 * @param csrfToken - 購読登録 API 用 CSRF トークン
 * @returns 購読・登録フローが完了したら `true`、VAPID 未設定などで失敗なら `false`
 */
async function ensurePushSubscriptionOnRegistration(
    registration: ServiceWorkerRegistration,
    csrfToken: string
): Promise<boolean> {
    const existingSub = await registration.pushManager.getSubscription();
    if (existingSub) {
        await subscribePush(existingSub, csrfToken);
        return true;
    }

    const vapidPublicKey = process.env.VITE_VAPID_PUBLIC_KEY;
    if (!vapidPublicKey) {
        console.error("VITE_VAPID_PUBLIC_KEY is not set");
        return false;
    }

    const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey:
            vapidPublicKeyToApplicationServerKey(vapidPublicKey),
    });
    await subscribePush(subscription, csrfToken);
    return true;
}

/**
 * Web Push の購読とバックエンド登録をまとめるフック。
 *
 * @param csrfToken - 購読・解除 API 呼び出し用の CSRF トークン
 * @returns `state`: 通知権限用状態。
 * `isSubscribed`: **権限が granted のときだけ** Query の結果を返し、それ以外は常に false（無効時のキャッシュを表示しない）。
 * `isSubscribedLoading`: **granted のときだけ** Query の取得中、それ以外は false。
 * `subscribe`: 購読開始
 * `unsubscribe`: ・購読解除。
 */
export const usePushSubscription = (csrfToken: string) => {
    const [state, setState] = useState<PushSubscriptionState>(
        getInitialPushSubscriptionState
    );
    const queryClient = useQueryClient();
    const { data: isSubscribedData, isLoading: isSubscribedLoading } = useQuery(
        {
            queryKey: webPushSubscriptionQueryKey,
            queryFn: fetchIsPushSubscribed,
        }
    );

    const isSubscribed = isSubscribedData ?? false;

    const invalidateSubscription = useCallback(async () => {
        await queryClient.invalidateQueries({
            queryKey: webPushSubscriptionQueryKey,
        });
    }, [queryClient]);

    /**
     * 通知権限を要求し、許可されれば Push を購読してバックエンドへ登録する。
     * @returns 非対応環境・拒否・エラー時は `false`、購読・登録が完了したら `true`
     */
    const subscribe = useCallback(async () => {
        if (!isPushApiSupported()) {
            return false;
        }
        try {
            const permission = await Notification.requestPermission();
            setState(notificationPermissionToUiState(permission));

            if (permission !== "granted") {
                return false;
            }

            const registration = await navigator.serviceWorker.ready;
            const ok = await ensurePushSubscriptionOnRegistration(
                registration,
                csrfToken
            );
            return ok;
        } catch (error) {
            console.error("Push subscription failed:", error);
            return false;
        } finally {
            await invalidateSubscription();
        }
    }, [csrfToken, invalidateSubscription]);

    /**
     * Push 購読を解除し、バックエンドからも削除を依頼する。
     * 成否にかかわらず最後に購読キャッシュを無効化する。
     */
    const unsubscribe = useCallback(async () => {
        if (!isPushApiSupported()) {
            return;
        }
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription =
                await registration.pushManager.getSubscription();
            if (subscription) {
                await unsubscribePush(subscription.endpoint, csrfToken);
                await subscription.unsubscribe();
            }
        } catch (error) {
            console.error("Push unsubscription failed:", error);
        } finally {
            await invalidateSubscription();
        }
    }, [csrfToken, invalidateSubscription]);

    return {
        state,
        isSubscribed,
        isSubscribedLoading,
        subscribe,
        unsubscribe,
    };
};
